import React, { useCallback, useEffect, useRef, useState } from 'react';
import { APIProvider, useMapsLibrary } from '@vis.gl/react-google-maps';
import {
  Building2, Loader2, LocateFixed, MapPin, Search, Star, TriangleAlert,
} from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription,
} from '@/components/ui/sheet';
import { Input } from '@/components/ui/input';

const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

function readCoordinate(value) {
  if (typeof value === 'function') return value();
  return value || 0;
}

function normalizePlace(place) {
  const derivedOpenNow = typeof place.opening_hours?.isOpen === 'function'
    ? place.opening_hours.isOpen()
    : null;

  return {
    name: place.name || place.displayName || '',
    address: place.vicinity || place.formatted_address || place.formattedAddress || '',
    location: {
      lat: readCoordinate(place.geometry?.location?.lat ?? place.location?.lat),
      lng: readCoordinate(place.geometry?.location?.lng ?? place.location?.lng),
    },
    placeId: place.place_id || place.id || '',
    phoneNumber: place.formatted_phone_number || place.international_phone_number || place.nationalPhoneNumber || '',
    website: place.website || place.websiteURI || '',
    rating: place.rating || 0,
    userRatingsTotal: place.user_ratings_total || place.userRatingCount || 0,
    photos: Array.isArray(place.photos) ? place.photos : [],
    businessStatus: place.business_status || place.businessStatus || '',
    types: place.types || [],
    openNow: typeof derivedOpenNow === 'boolean' ? derivedOpenNow : null,
  };
}

function formatPrimaryType(types = []) {
  const raw = types[0];
  if (!raw) return 'Local business';
  return raw.replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}

function NearbyBusinessFinderContent({ open, onAddBusiness }) {
  const placesLib = useMapsLibrary('places');
  const requestedLocationRef = useRef(false);
  const [locationMode, setLocationMode] = useState('idle');
  const [locationQuery, setLocationQuery] = useState('');
  const [locationLabel, setLocationLabel] = useState('');
  const [results, setResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [addingPlaceId, setAddingPlaceId] = useState('');

  const getPlaceDetails = useCallback((service, placeId) => new Promise((resolve) => {
    service.getDetails(
      {
        placeId,
        fields: [
          'name',
          'formatted_address',
          'geometry',
          'place_id',
          'formatted_phone_number',
          'website',
          'rating',
          'user_ratings_total',
          'photos',
          'business_status',
          'types',
          'opening_hours',
          'utc_offset_minutes',
        ],
      },
      (place, status) => {
        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !place) {
          resolve(null);
          return;
        }
        resolve(place);
      },
    );
  }), []);

  const searchNearbyBusinesses = useCallback((center, label) => {
    if (!window.google?.maps?.places) {
      setSearchError('Google Maps did not finish loading. Please try again.');
      return;
    }

    setIsSearching(true);
    setSearchError('');
    setLocationLabel(label);

    const service = new window.google.maps.places.PlacesService(document.createElement('div'));
    service.nearbySearch(
      {
        location: center,
        radius: 4000,
      },
      (places, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.ZERO_RESULTS) {
          setIsSearching(false);
          setResults([]);
          setSearchError('No nearby businesses were found for this area.');
          return;
        }

        if (status !== window.google.maps.places.PlacesServiceStatus.OK || !places?.length) {
          setIsSearching(false);
          setResults([]);
          setSearchError('We could not load nearby businesses for that area.');
          return;
        }

        const nearbyPlaces = places.slice(0, 12);

        Promise.all(
          nearbyPlaces.map(async (place) => {
            const fallbackPlace = normalizePlace(place);
            if (!place.place_id) return fallbackPlace;

            const details = await getPlaceDetails(service, place.place_id);
            return details
              ? { ...fallbackPlace, ...normalizePlace(details) }
              : fallbackPlace;
          }),
        )
          .then((resolvedPlaces) => {
            setResults(
              resolvedPlaces.filter((place) => place.name && place.location.lat && place.location.lng),
            );
          })
          .finally(() => {
            setIsSearching(false);
          });
      },
    );
  }, [getPlaceDetails]);

  const geocodeLocation = useCallback((query) => new Promise((resolve, reject) => {
    if (!window.google?.maps?.Geocoder) {
      reject(new Error('Google Maps did not finish loading.'));
      return;
    }

    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ address: query }, (matches, status) => {
      if (status !== 'OK' || !matches?.length) {
        reject(new Error('We could not find that location.'));
        return;
      }
      resolve(matches[0]);
    });
  }), []);

  const handleManualSearch = useCallback(async () => {
    if (!locationQuery.trim()) {
      toast.error('Enter a city, ZIP code, or neighborhood to search nearby businesses.');
      return;
    }

    try {
      setIsSearching(true);
      setSearchError('');
      const match = await geocodeLocation(locationQuery.trim());
      const center = {
        lat: readCoordinate(match.geometry?.location?.lat),
        lng: readCoordinate(match.geometry?.location?.lng),
      };
      setLocationMode('manual');
      searchNearbyBusinesses(center, `Businesses near ${match.formatted_address}`);
    } catch (error) {
      setIsSearching(false);
      setResults([]);
      setSearchError(error.message || 'We could not find that location.');
    }
  }, [geocodeLocation, locationQuery, searchNearbyBusinesses]);

  const requestCurrentLocation = useCallback(() => {
    if (!navigator.geolocation) {
      setLocationMode('manual');
      setSearchError('Location access is unavailable on this device. Enter a place manually.');
      return;
    }

    setLocationMode('requesting');
    setSearchError('');

    navigator.geolocation.getCurrentPosition(
      ({ coords }) => {
        const center = { lat: coords.latitude, lng: coords.longitude };
        setLocationMode('granted');
        searchNearbyBusinesses(center, 'Businesses near your current location');
      },
      () => {
        setLocationMode('manual');
        setSearchError('Location access was denied. Enter a city, ZIP code, or neighborhood instead.');
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 300000,
      },
    );
  }, [searchNearbyBusinesses]);

  useEffect(() => {
    if (!open) {
      requestedLocationRef.current = false;
      setLocationMode('idle');
      setLocationQuery('');
      setLocationLabel('');
      setResults([]);
      setSearchError('');
      setIsSearching(false);
      setAddingPlaceId('');
      return;
    }

    if (!placesLib || requestedLocationRef.current) return;
    requestedLocationRef.current = true;
    requestCurrentLocation();
  }, [open, placesLib, requestCurrentLocation]);

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-4 overflow-hidden">
      <div className="shrink-0 space-y-2">
        <div className="flex gap-2">
          <div className="relative flex-1">
            <Search className="absolute top-1/2 left-3 h-4 w-4 -translate-y-1/2 text-slate-400" />
            <Input
              value={locationQuery}
              onChange={(event) => setLocationQuery(event.target.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') handleManualSearch();
              }}
              placeholder="City, ZIP, or neighborhood"
              className="h-11 rounded-xl border-slate-200 bg-slate-50 pl-9"
            />
          </div>
          <Button
            variant="outline"
            onClick={requestCurrentLocation}
            disabled={locationMode === 'requesting' || isSearching}
            className="h-11 shrink-0 rounded-xl bg-white"
          >
            {locationMode === 'requesting' ? <Loader2 className="h-4 w-4 animate-spin" /> : <LocateFixed className="h-4 w-4" />}
            <span className="hidden sm:inline">My location</span>
          </Button>
          <Button
            onClick={handleManualSearch}
            disabled={isSearching || !locationQuery.trim()}
            className="h-11 shrink-0 rounded-xl bg-slate-950 text-white hover:bg-slate-800"
          >
            {isSearching && locationMode !== 'requesting' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
            <span className="hidden sm:inline">Search</span>
          </Button>
        </div>

        {searchError ? (
          <div className="flex items-start gap-2 rounded-xl border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800">
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <span>{searchError}</span>
          </div>
        ) : null}

        <div className="flex items-center justify-between gap-3">
          <p className="text-sm text-slate-500">
            {results.length > 0
              ? `${results.length} results${locationLabel ? ` · ${locationLabel}` : ''}`
              : (locationLabel || 'Enter a location to search')}
          </p>
          {isSearching ? (
            <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-400" />
          ) : null}
        </div>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto pr-1">
        {results.length > 0 ? results.map((place) => (
          <div key={place.placeId || `${place.name}-${place.address}`} className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="min-w-0">
              <div className="flex flex-wrap items-center gap-2">
                <p className="text-sm font-semibold text-slate-950">{place.name}</p>
                <Badge variant="outline" className="rounded-full bg-slate-50 text-xs text-slate-600">
                  {formatPrimaryType(place.types)}
                </Badge>
                {place.openNow === true ? (
                  <Badge variant="outline" className="rounded-full border-emerald-200 bg-emerald-50 text-xs text-emerald-700">
                    Open now
                  </Badge>
                ) : null}
              </div>

              <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
                {place.address ? (
                  <div className="flex items-start gap-1.5">
                    <MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />
                    <span className="min-w-0 break-words">{place.address}</span>
                  </div>
                ) : null}
                {place.rating > 0 ? (
                  <div className="flex items-center gap-1.5">
                    <Star className="h-4 w-4 fill-amber-400 text-amber-400" />
                    <span>{place.rating.toFixed(1)}{place.userRatingsTotal ? ` (${place.userRatingsTotal})` : ''}</span>
                  </div>
                ) : null}
              </div>

              <Button
                onClick={async (e) => {
                  e.stopPropagation();
                  try {
                    setAddingPlaceId(place.placeId);
                    await onAddBusiness(place);
                  } finally {
                    setAddingPlaceId('');
                  }
                }}
                disabled={addingPlaceId === place.placeId}
                className="mt-3 w-full rounded-xl bg-coral text-white hover:bg-coral/90 sm:w-auto"
              >
                {addingPlaceId === place.placeId ? <Loader2 className="h-4 w-4 animate-spin" /> : <Building2 className="h-4 w-4" />}
                Add to workspace
              </Button>
            </div>
          </div>
        )) : (
          <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-8 text-center text-sm text-slate-500">
            {isSearching ? 'Looking for nearby businesses...' : 'No businesses loaded yet.'}
          </div>
        )}
      </div>
    </div>
  );
}

export default function NearbyBusinessFinderDialog({
  open,
  onOpenChange,
  onAddBusiness,
}) {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 640);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const apiMissing = !apiKey || apiKey === 'your_api_key_here';
  const content = apiMissing ? (
    <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
      Add `VITE_GOOGLE_MAPS_API_KEY` to enable nearby business discovery.
    </div>
  ) : (
    <APIProvider apiKey={apiKey} version="weekly" loading="async">
      <NearbyBusinessFinderContent open={open} onAddBusiness={onAddBusiness} />
    </APIProvider>
  );

  if (isMobile) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent side="bottom" className="flex h-[92dvh] flex-col overflow-hidden rounded-t-2xl p-5">
          <SheetHeader className="shrink-0 p-0">
            <SheetTitle>Find businesses around me</SheetTitle>
            <SheetDescription>
              Search nearby or enter a location, then add any business.
            </SheetDescription>
          </SheetHeader>
          {content}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[calc(100dvh-2rem)] max-w-3xl flex-col overflow-hidden">
        <DialogHeader className="shrink-0">
          <DialogTitle>Find businesses around me</DialogTitle>
          <DialogDescription>
            Search nearby or enter a location, then add any business to the workspace.
          </DialogDescription>
        </DialogHeader>
        {content}
      </DialogContent>
    </Dialog>
  );
}
